import { useState, useCallback, useEffect, useRef } from 'react';
import { CommonEvaluationRequest as BackendEvaluationRequest, CommonEvaluation, /* CommonSkillField, */ CommonUser } from '@codidae/common-types'; // Renommer pour éviter conflit
import { chatbotRequest, getChatbotRequests } from 'services/evaluations';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// --- COPIE TEMPORAIRE DES TYPES BACKEND --- 
// TODO: Déplacer vers @codidae/common-types
export enum UiState {
  INITIAL_PROMPT = 'INITIAL_PROMPT',
  AWAITING_FIELD = 'AWAITING_FIELD',
  AWAITING_DATE = 'AWAITING_DATE',
  AWAITING_CHAPTERS = 'AWAITING_CHAPTERS',
  AWAITING_CONFIRMATION = 'AWAITING_CONFIRMATION',
  COMPLETED_IDLE = 'COMPLETED_IDLE',
  FREE_DISCUSSION = 'FREE_DISCUSSION',
  SHOWING_MESSAGE = 'SHOWING_MESSAGE',
  ERROR = 'ERROR',
  FREE_DISCUSSION_MODE = 'FREE_DISCUSSION_MODE',
}

interface MessageDto {
  role: 'user' | 'assistant';
  content: string;
}

// Adapter pour utiliser les types de common-types si possible
interface EvaluationChatbotRequestForHook extends Omit<BackendEvaluationRequest, 'evaluation' | 'messages' | 'user' | 'children'> {
    messages: MessageDto[];
    evaluation?: CommonEvaluation; // Utiliser le type commun
    // S'assurer que children/user sont bien présents si nécessaire
    children: { id: number }; 
    user: CommonUser;
    isFreeChatMode: boolean;
}

export interface ChatbotResponse {
  request: EvaluationChatbotRequestForHook; // Utiliser le type adapté
  uiState: UiState;
  assistantMessage: string;
}
// --- FIN COPIE TEMPORAIRE ---


// Définition type Message local (inchangé)
interface Message extends MessageDto {}

// Payload pour envoyer au backend (INCHANGÉ côté backend, mais le frontend doit pouvoir envoyer les IDs)
interface BackendChatbotRequestDto {
  childrenId: number;
  message?: string;
  requestId?: number;
  selectedChapterIds?: number[]; // Ajouter ici pour correspondre au DTO
}

// Payload pour appeler sendMessage (INCHANGÉ côté backend, mais le frontend doit pouvoir envoyer les IDs)
interface SendMessageParams {
  message: string;
  childrenId: number;
  selectedChapterIds?: number[]; // Ajouter le champ optionnel
}

export function useChatbot(childrenId: number, requestId?: number, onRequestCreated?: (id: number) => void) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Utiliser le type adapté ici aussi
  const [currentRequest, setCurrentRequest] = useState<EvaluationChatbotRequestForHook | null>(null);
  const [uiState, setUiState] = useState<UiState>(UiState.INITIAL_PROMPT);

  const [errorState, setErrorState] = useState<{ attempt: number; countdown: number | null }>({ attempt: 0, countdown: null });
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();

  // Utiliser le type adapté pour la query
  const { data: requests, refetch: refetchRequests } = useQuery<EvaluationChatbotRequestForHook[]>({ 
    queryKey: ['getChatbotRequests'],
    queryFn: getChatbotRequests,
    refetchInterval: 30000,
    staleTime: 30000,
  });

  useEffect(() => {
    console.log(`[HOOK][useEffect] Dependency change detected - requestId: ${requestId}, requests loaded: ${!!requests}`);
    if (requestId && Array.isArray(requests)) {
        const matchingRequest = requests.find(req => {
            const { id } = req;
            return id === requestId;
        });
        if (matchingRequest && (!currentRequest || currentRequest.id !== requestId)) {
            console.log(`[HOOK][useEffect] Request ${requestId} found. Setting state.`);
            setMessages(matchingRequest.messages as Message[]);
            setCurrentRequest(matchingRequest); // Doit être du type EvaluationChatbotRequestForHook
            if (matchingRequest.isFreeChatMode) {
                console.log(`[HOOK][useEffect] Request ${requestId} is in Free Chat Mode. Setting UI state.`);
                setUiState(UiState.FREE_DISCUSSION_MODE);
            } else {
                const { evaluation } = matchingRequest;
                let determinedUiState = UiState.INITIAL_PROMPT; // Default
                if (matchingRequest.isCompleted) {
                    determinedUiState = UiState.COMPLETED_IDLE;
                } else if (evaluation) {
                    if (!evaluation.field) {
                        determinedUiState = UiState.AWAITING_FIELD;
                    } else if (evaluation.date === undefined || evaluation.date === null) {
                        determinedUiState = UiState.AWAITING_DATE;
                    } else if (!Array.isArray(evaluation.chapters) || evaluation.chapters.length === 0) {
                        determinedUiState = UiState.AWAITING_CHAPTERS;
                    } else {
                        determinedUiState = UiState.AWAITING_CONFIRMATION;
                    }
                } else {
                    console.warn(`[HOOK][useEffect] Request ${requestId} found but evaluation is null. Defaulting to AWAITING_FIELD.`);
                    determinedUiState = UiState.AWAITING_FIELD;
                }
                console.log(`[HOOK][useEffect] Determined initial UiState (Eval Mode): ${determinedUiState}`);
                setUiState(determinedUiState);
            }
        } else if (!matchingRequest) {
             console.warn(`[HOOK][useEffect] Request ${requestId} not found. Resetting.`);
             setMessages([]);
             setCurrentRequest(null);
             setUiState(UiState.INITIAL_PROMPT);
        }
    } else if (!requestId && currentRequest) {
        console.log('[HOOK][useEffect] No requestId provided. Resetting state.');
        setMessages([]);
        setCurrentRequest(null);
        setUiState(UiState.INITIAL_PROMPT);
    }
  }, [requestId, requests, currentRequest]);

  const chatbotRequestMutation = useMutation<
    ChatbotResponse,
    Error,
    BackendChatbotRequestDto
  >({
    mutationFn: chatbotRequest as (payload: BackendChatbotRequestDto) => Promise<ChatbotResponse>,
    retry: false,
    onSuccess: (data, variables) => {
      console.log('[useChatbot] chatbotRequestMutation - Succès:', {
        responseId: data?.request?.id,
        sentRequestId: variables.requestId,
        returnedUiState: data?.uiState,
        messageCount: data?.request?.messages?.length,
        assistantMessageReceived: data?.assistantMessage
      });

      if (data?.request?.messages) {
        // CORRECTION: Préserver le dernier message utilisateur si nécessaire
        const lastUserMessage = messages.length > 0 && messages[messages.length - 1].role === 'user' 
          ? messages[messages.length - 1] 
          : null;
        
        // Récupérer les messages du backend
        const backendMessages = data.request.messages as Message[];
        
        // Vérifier si le dernier message utilisateur est déjà dans les messages du backend
        const lastUserMessageInBackend = lastUserMessage 
          ? backendMessages.some(m => 
              m.role === 'user' && 
              m.content === lastUserMessage.content && 
              backendMessages.indexOf(m) === backendMessages.length - 2  // Avant-dernier message (dernier étant la réponse assistant)
            )
          : true;
        
        if (lastUserMessage && !lastUserMessageInBackend) {
          console.log('[useChatbot] Préservation du dernier message utilisateur qui n\'est pas encore dans la réponse backend');
          setMessages([...backendMessages, lastUserMessage]);
        } else {
          setMessages(backendMessages);
        }
      }
      
      if (data?.request) {
        // S'assurer que data.request est compatible avec EvaluationChatbotRequestForHook
        setCurrentRequest(data.request);
        if (!variables.requestId && data.request.id && onRequestCreated) {
          console.log('[useChatbot] Nouvelle requête créée, ID:', data.request.id);
          onRequestCreated(data.request.id);
        }
      }
      if (data?.uiState) {
        setUiState(data.uiState);
      }

      queryClient.invalidateQueries({ queryKey: ['getChatbotRequests'] });
      if (data?.request?.children?.id) {
         queryClient.invalidateQueries({ queryKey: ['getEvaluations', data.request.children.id] });
      }

      setIsLoading(false);
      setErrorState({ attempt: 0, countdown: null });
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    },
    onError: (error, variables) => {
      console.error(`[useChatbot] chatbotRequestMutation - Erreur (onError):`, error);
      setUiState(UiState.ERROR);
      setIsLoading(false);
      setMessages(prev => [
          ...prev,
          { role: 'assistant', content: "Désolé, une erreur technique est survenue." }
      ]);
      // Gérer retry?
      if (errorState.attempt < 2) {
           setErrorState(prev => ({ ...prev, attempt: prev.attempt + 1 }));
           console.log("Error state updated, attempt:", errorState.attempt + 1);
       } else {
            console.error("Max retries reached after error.");
            setErrorState({ attempt: 0, countdown: null });
       }
    }
  });

  const sendMessage = useCallback(async (
    payload: SendMessageParams
  ) => {
    const currentRequestId = requestId;
    const { message, childrenId: payloadChildrenId, selectedChapterIds } = payload;
    console.log(`[HOOK][sendMessage] Called with message: "${message}", childrenId: ${payloadChildrenId}, current requestId: ${currentRequestId}, selectedChapterIds:`, selectedChapterIds);

    if (!payloadChildrenId) {
        console.error('[HOOK][sendMessage] childrenId is missing!');
        return;
    }

    if (message.trim() || messages.length === 0) {
        const userMessage: Message = { role: 'user', content: message };
        setMessages(prev => [...prev, userMessage]);
    }

    setIsLoading(true);
    setErrorState({ attempt: 0, countdown: null });
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    const backendPayload: BackendChatbotRequestDto = {
      childrenId: payloadChildrenId,
      requestId: currentRequestId,
      message
    };

    if (selectedChapterIds !== undefined) {
        backendPayload.selectedChapterIds = selectedChapterIds;
    }

    console.log('[HOOK][sendMessage] FINAL backendPayload being sent:', JSON.stringify(backendPayload));

    try {
      console.log(`[HOOK][sendMessage] Calling mutation.mutateAsync...`);
      await chatbotRequestMutation.mutateAsync(backendPayload);
      console.log(`[HOOK][sendMessage] mutateAsync finished.`);
    } catch (error) {
      console.error(`[HOOK][sendMessage] mutateAsync FAILED (handled by onError).`);
    }
  }, [requestId, chatbotRequestMutation, messages.length]);

  useEffect(() => 
    () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    }
  , []);

  return {
    messages,
    setMessages,
    isLoading,
    loadingState: isLoading ? 'loading' : 'idle',
    currentRequest,
    uiState,
    sendMessage,
    refetchRequests,
    requests
  };
} 